import React from "react";
import { Mail, Users, MapPin, Cookie, Lock } from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import LogoMR from "@/components/landing/LogoMR";
import { TID } from "@/constants/testIds";
import { openCookiePreferences } from "@/components/landing/CookieConsent";

const COMMUNITY_URL = "https://chat.whatsapp.com/BYtWElK92VpIFAHOAd1H4X";
const PHONE_WA = "393773660476";
const PHONE_DISPLAY = "+39 377 366 0476";
const EMAIL = "metododimare@gmail.com";

export default function Footer() {
  return (
    <footer
      data-testid={TID.footer}
      className="bg-[#0B192C] text-[#F9F8F6] pt-24 pb-12 border-t border-[#0B192C]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="border-b border-[#F9F8F6]/15 pb-12">
          <div className="mb-8 inline-block">
            <LogoMR size={110} bg="transparent" />
          </div>
          <p className="font-serif-display text-[18vw] md:text-[14vw] leading-[0.85] tracking-tight text-[#F9F8F6]">
            Metodo<span className="text-[#D4B895]">·</span>di
            <span className="text-[#D4B895]">·</span>Mare
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-10 pt-12">
          <div className="md:col-span-5">
            <p className="overline text-[#D4B895]">Manifesto</p>
            <p className="font-serif-display text-2xl md:text-3xl mt-4 leading-snug">
              Una bussola per chi sceglie il digitale come professione, non
              come moda.
            </p>
            <p className="mt-6 text-sm font-light text-[#F9F8F6]/70">
              di Michele Roccaro · Imprenditore Digitale · Formatore
            </p>
          </div>

          <div className="md:col-span-2 md:col-start-7">
            <p className="overline text-[#D4B895]">Navigazione</p>
            <ul className="mt-4 space-y-2 text-sm font-light text-[#F9F8F6]/80">
              <li>
                <a href="#method" className="link-underline">Il Metodo</a>
              </li>
              <li>
                <a href="#author" className="link-underline">L'Autore</a>
              </li>
              <li>
                <a href="#reviews" className="link-underline">Recensioni</a>
              </li>
              <li>
                <a href="#blog" className="link-underline">Blog</a>
              </li>
              <li>
                <a href="#faq" className="link-underline">FAQ</a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="overline text-[#D4B895]">Legali</p>
            <ul className="mt-4 space-y-2 text-sm font-light text-[#F9F8F6]/80">
              <li>
                <a href="/privacy" className="link-underline" data-testid="footer-privacy">Privacy Policy</a>
              </li>
              <li>
                <a href="/cookie" className="link-underline" data-testid="footer-cookie">Cookie Policy</a>
              </li>
              <li>
                <a href="/termini" className="link-underline" data-testid="footer-terms">Termini di vendita</a>
              </li>
              <li>
                <a href="/note-legali" className="link-underline" data-testid="footer-imprint">Note Legali</a>
              </li>
              <li>
                <button
                  onClick={openCookiePreferences}
                  className="inline-flex items-center gap-1 link-underline"
                  data-testid="footer-cookie-prefs"
                >
                  <Cookie size={11} />
                  Preferenze cookie
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline text-[#D4B895]">Contatti</p>
            <ul className="mt-4 space-y-3">
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  className="inline-flex items-center gap-2 text-sm font-light text-[#F9F8F6]/80 hover:text-[#D4B895]"
                  data-testid="footer-email"
                >
                  <Mail size={14} />
                  {EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${PHONE_WA}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-light text-[#F9F8F6]/80 hover:text-[#D4B895]"
                  data-testid="footer-whatsapp"
                >
                  <WhatsAppIcon size={14} className="text-[#25D366]" />
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={COMMUNITY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-light text-[#F9F8F6]/80 hover:text-[#D4B895]"
                  data-testid="footer-community"
                >
                  <Users size={14} />
                  Community WhatsApp
                </a>
              </li>
              <li className="inline-flex items-start gap-2 text-sm font-light text-[#F9F8F6]/60 pt-2">
                <MapPin size={14} className="mt-0.5" />
                Roma · Italia
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#F9F8F6]/15 flex flex-col md:flex-row justify-between items-center gap-4 text-xs uppercase tracking-[0.2em] text-[#F9F8F6]/60">
          <p>© 2026 Michele Roccaro · Metodo di Mare · C.F. RCCMHL86M13I535N</p>
          <div className="flex items-center gap-6">
            <p>Scicli (RG) · A.C.D.T. · Legge 4/2013</p>
            <a
              href="/admin/login"
              data-testid="footer-admin-link"
              title="Area riservata amministratore"
              className="inline-flex items-center gap-1.5 text-[#F9F8F6]/40 hover:text-[#D4B895] transition-colors"
            >
              <Lock size={11} />
              Area Riservata
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
